import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {LoggerRessource} from '../../../../../micromate-learn-lib/src/lib/services/rest/logger-ressource.service';
import {TeamsContextService} from '../../core/teams-context.service';
import {LearnerRessource} from '../../../../../micromate-learn-lib/src/lib/services/rest/learner-ressource.service';
import {TranslateService} from '@ngx-translate/core';
import {Title} from '@angular/platform-browser';
import {EventRecorderService} from '../../../../../micromate-learn-lib/src/lib/services/event/event-recorder.service';
import {Translation} from '../../../../../micromate-learn-lib/src/lib/services/Translation';
import {TabEventType} from '../../../../../micromate-learn-lib/src/lib/services/event/TabEventType';
import {LearnerProfileResponseDto} from '../../../../../micromate-learn-lib/src/lib/types/LearnerProfileResponseDto';
import {ProfileRessource} from '../../../../../micromate-learn-lib/src/lib/services/rest/profile-ressource.service';
import {PseudonymSettings} from '../../../../../micromate-learn-lib/src/lib/components/profile/PseudonymSettings';
import {
    ProfilePseudonymService
} from '../../../../../micromate-learn-lib/src/lib/components/profile/profile-pseudonym.service';
import {app} from '@microsoft/teams-js';
import {ToastrService} from 'ngx-toastr';
import {
    UILocalizationService
} from '../../../../../micromate-learn-lib/src/lib/services/locale/u-i-localization.service';
import {filter, map} from 'rxjs/operators';
import {
    UpdateLearnerProfileMainChannelRequestDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/UpdateLearnerProfileMainChannelRequestDto';
import {
    UpdateProfileLocaleRequestDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/UpdateProfileLocaleRequestDto';
import {
    UpdateLearningReminderRequestDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/UpdateLearningReminderRequestDto';
import {BrandingService} from '../../../../../micromate-learn-lib/src/lib/services/branding/branding.service';
import {ProfileLearnerOrganizationService} from '../profile-learner-missing/profile-learner-organization.service';

@Component({
    selector: 'mm-profile-profile',
    templateUrl: './profile-profile.component.html',
    styleUrls: ['profile-profile.component.scss'],
    standalone: false
})
export class ProfileProfileComponent implements OnInit, OnDestroy {
    public teamsContextServiceSubscription = new Subscription();
    public profileData: BehaviorSubject<LearnerProfileResponseDto | undefined> = new BehaviorSubject<LearnerProfileResponseDto | undefined>(undefined);
    public profileData$ = this.profileData.pipe(filter((value): value is LearnerProfileResponseDto => value !== undefined));

    public locale: Subject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
    public locale$ = this.locale.pipe(filter((value): value is string => value !== undefined));
    private userId!: string;
    public isLearnerMissing = false;
    private profileUpdated: Subject<void> = new Subject<void>();
    public profileUpdated$: Observable<void> = this.profileUpdated.asObservable();

    public mainChannel$ = this.profileData$.pipe(filter((data): data is LearnerProfileResponseDto => data !== undefined),
        map(profileData => profileData.mainChannel));
    public allChannels$ = this.profileData$.pipe(filter((data): data is LearnerProfileResponseDto => data !== undefined),
        map(profileData => profileData.channels));

    constructor(public teamsContextService: TeamsContextService,
                private learnerService: LearnerRessource,
                private translate: TranslateService,
                private ref: ChangeDetectorRef,
                private titleService: Title,
                private eventRecorderService: EventRecorderService,
                private uiLocalizationService: UILocalizationService,
                private loggerService: LoggerRessource,
                private profileService: ProfileRessource,
                private profilePseudonymService: ProfilePseudonymService,
                private toastr: ToastrService,
                public brandingService: BrandingService,
                private profileLearnerOrganizationService: ProfileLearnerOrganizationService) {
        this.titleService.setTitle('Micromate Profile');
    }

    public ngOnInit(): void {
        this.loggerService.logFrontendInfoToBackend('ProfileProfile init');
        this.teamsContextServiceSubscription = this.teamsContextService.teamsContext$.subscribe(async teamsContext => {
            this.userId = teamsContext.user!.id;
            this.loggerService.logFrontendInfoToBackend(`ProfileProfile ${JSON.stringify(teamsContext)}, userId: ${this.userId}`);
            if (this.userId === undefined) {
                throw new Error(`UserObjectId is undefined when open ProfileProfile, teamsContext: ${JSON.stringify(teamsContext)}`);
            }
            // eslint-disable-next-line no-null/no-null
            if (this.userId === null) {
                throw new Error(`UserObjectId is null when open ProfileProfile, teamsContext: ${JSON.stringify(teamsContext)}`);
            }
            if (this.userId.trim().length === 0) {
                throw new Error(`UserObjectId is empty when open ProfileProfile, teamsContext: ${JSON.stringify(teamsContext)}`);
            }
            await this.getLearnerData(this.userId, teamsContext);
            if (!this.isLearnerMissing) {
                await this.recordTabEvents(this.userId, teamsContext.page.subPageId);
            }
        });
    }


    public ngOnDestroy(): void {
        this.loggerService.logFrontendInfoToBackend('ProfileProfile destroyed');
        if (this.teamsContextServiceSubscription !== undefined) {
            this.teamsContextServiceSubscription.unsubscribe();
        }
    }

    public savePseudonymSettings(pseudonymSettings: PseudonymSettings): void {
        this.profileService.savePseudonymSettings(this.userId, pseudonymSettings).subscribe(async () => {
            const learnerProfilePromise = this.profileService.getLearnerProfile(this.userId);
            this.profileData.next(await learnerProfilePromise);
            this.ref.detectChanges();
        });
    }

    public generatePseudonym(): void {
        this.profilePseudonymService.pseudonymGenerationSubscription = this.profileService.generatePseudonym(this.userId).subscribe(pseudonym => {
            this.profilePseudonymService.setNewPseudonym(pseudonym);
            this.ref.detectChanges();
        });
    }

    public changeMonthlyReport(monthlyReportState: boolean): void {
        this.profileService.saveMonthlyLearningReportStatus(this.userId, monthlyReportState).subscribe(async () => {
            const learnerProfilePromise = this.profileService.getLearnerProfile(this.userId);
            this.profileData.next(await learnerProfilePromise);
            this.ref.detectChanges();
            this.toastr.success(this.translate.instant('profile.monthlyReportSaved') as string);
        });
    }

    private async getLearnerData(msteamsId: string, teamsContext: app.Context): Promise<void> {
        this.isLearnerMissing = !(await this.profileLearnerOrganizationService.applyLearnerOrganizationBranding(msteamsId, teamsContext));
        if (this.isLearnerMissing) {
            this.ref.detectChanges();
            return;
        }

        const languageDataPromise = this.learnerService.getLearnerLanguage(msteamsId, teamsContext);
        const learnerProfilePromise = this.profileService.getLearnerProfile(msteamsId);
        const languageData = await languageDataPromise;
        if (languageData === undefined) {
            throw new Error(`Could not find language in profile for msteamsId: ${msteamsId}`);
        }
        this.translate.use(new Translation().getTranslationLanguage(languageData.language));
        const profileData = await learnerProfilePromise;
        this.profileData.next(profileData);
        this.locale.next(profileData.locale);
        this.ref.detectChanges();
    }

    private async recordTabEvents(userId: string, subEntityId: string | undefined | {
        openInConversation: boolean;
    }): Promise<void> {
        await this.eventRecorderService.recordTabOpenedEvent(userId, TabEventType.TabProfileOpened,
            this.eventRecorderService.createTabOpenedEventPayload(subEntityId));
    }

    public async saveLocalData(data: UpdateProfileLocaleRequestDto): Promise<void> {
        const updatedProfile = await this.profileService.updateLocalForTeams(this.userId, data);
        await this.uiLocalizationService.setLanguage(updatedProfile.locale.split('-')[0]);
        this.locale.next(updatedProfile.locale);
        this.profileData.next(updatedProfile);
        this.profileUpdated.next();
    }

    public async saveMainChannel(data: UpdateLearnerProfileMainChannelRequestDto): Promise<void> {
        const updatedProfile = await this.profileService.updateMainChannelForTeams(this.userId, data);
        this.profileData.next(updatedProfile);
        this.profileUpdated.next();
    }

    public async saveLearningReminder(data: UpdateLearningReminderRequestDto): Promise<void> {
        const updatedProfile = await this.profileService.updateLearningReminderForTeams(this.userId, data);
        this.profileData.next(updatedProfile);
        this.ref.detectChanges();
    }
}
